import Image from '@atoms/Image/Image';
import { ICustomisationOption } from '@models/IProductCategoriesAndOptions';
import convertHexToRgba from '@utils/helpers/convertHexToRgba';
import updateDataLayer from '@utils/helpers/gtm';
import classNames from 'classnames';

interface Props {
  readonly categoryType: string;
  readonly dataTestId?: string;
  readonly hasSingleCustomisationCategory: boolean;
  readonly options: ICustomisationOption[];
  readonly selectedOption?: ICustomisationOption;
  readonly onOptionClick: (optionId: string, optionName: string) => void;
}

const DefaultOptionsList = ({
  categoryType,
  dataTestId,
  hasSingleCustomisationCategory,
  options,
  selectedOption,
  onOptionClick,
}: Props) => (
  <div
    className="flex flex-col flex-wrap gap-4"
    data-testid={`${dataTestId}-Options`}
  >
    {options.map(
      ({ name, thumbnail, optionId, colour, price, materialCode }, index) => {
        const isSelectedOption = hasSingleCustomisationCategory
          ? selectedOption?.materialCode === materialCode
          : selectedOption?.optionId === optionId;
        const isWhiteColour = colour === '#FFF' || colour === '#FFFFFF';
        const customBorderColour = isWhiteColour ? '#000' : colour;

        return (
          <button
            style={{
              ...(isSelectedOption
                ? {
                    borderColor:
                      colour || (!thumbnail ? customBorderColour : '#939598'),
                    backgroundColor:
                      colour || (!thumbnail && colour && !isWhiteColour)
                        ? convertHexToRgba(colour, 0.1)
                        : '#F1F2F2',
                  }
                : {}),
            }}
            key={hasSingleCustomisationCategory ? materialCode : optionId}
            className={classNames(
              'flex items-center gap-2.5 rounded-[10px] border p-4 transition-colors',
              isSelectedOption ? 'pointer-events-none' : 'border-grey-200'
            )}
            onClick={() => {
              updateDataLayer({
                event: 'productModification',
                content_type: categoryType,
                selection: name,
              });

              onOptionClick(
                hasSingleCustomisationCategory ? materialCode : optionId,
                name
              );
            }}
            data-testid={`${dataTestId}-Option-${index}`}
          >
            {thumbnail && (
              <Image
                image={thumbnail}
                classes="shrink-0"
                imageClasses="h-5 w-5 rounded-full"
                dataTestId={`${dataTestId}-Option-${index}-Thumbnail`}
              />
            )}

            {!thumbnail && colour && (
              <span
                className={classNames('h-5 w-5 shrink-0 rounded-full', {
                  'border border-black': isWhiteColour,
                })}
                style={{ backgroundColor: colour }}
                data-testid={`${dataTestId}-Option-${index}-Colour`}
              />
            )}

            <p
              className="body-three grow text-left"
              data-testid={`${dataTestId}-Option-${index}-Name`}
            >
              {name}
            </p>

            {price && (
              <div className="flex flex-wrap justify-end gap-x-2.5 text-right">
                {price.formattedCalculatedPrice && (
                  <p
                    className="body-three body-three--bold text-black-100"
                    data-testid={`${dataTestId}-Option-${index}-Formatted-Calculated-Price`}
                  >
                    {price.formattedCalculatedPrice}
                  </p>
                )}

                {price.amountSaved > 0 && price.formattedPrice && (
                  <span
                    className="body-three body-three--bold text-grey-500 line-through"
                    data-testid={`${dataTestId}-Option-${index}-Formatted-Price`}
                  >
                    {price.formattedPrice}
                  </span>
                )}
              </div>
            )}
          </button>
        );
      }
    )}
  </div>
);

export default DefaultOptionsList;
